"use client";

import { type FormEvent, useEffect, useState } from "react";

import { ApiRequestError } from "@/lib/auth-api";
import {
    loadOpenAICompatibleConfig,
    saveOpenAICompatibleConfig,
    testOpenAICompatibleConfig,
    type OpenAICompatibleConfig,
    type OpenAICompatibleTestResult,
} from "@/lib/integration-api";

interface IntegrationsViewProps {
    csrfToken: string | null;
    onAgentSaved?: () => Promise<void> | void;
}

export function IntegrationsView({ csrfToken, onAgentSaved }: IntegrationsViewProps) {
    const [config, setConfig] = useState<OpenAICompatibleConfig | null>(null);
    const [agentName, setAgentName] = useState("");
    const [baseUrl, setBaseUrl] = useState("");
    const [modelId, setModelId] = useState("");
    const [apiKey, setApiKey] = useState("");
    const [testResult, setTestResult] = useState<OpenAICompatibleTestResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [message, setMessage] = useState<{ kind: "success" | "error"; text: string } | null>(null);

    useEffect(() => {
        let active = true;
        void loadOpenAICompatibleConfig()
            .then((loaded) => {
                if (!active) return;
                setConfig(loaded);
                setAgentName(loaded.agent_name ?? "");
                setBaseUrl(loaded.base_url ?? "");
                setModelId(loaded.model_id ?? "");
            })
            .catch((error: unknown) => {
                if (active) setMessage({ kind: "error", text: integrationErrorMessage(error) });
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    async function save(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        const token = validateCurrentForm();
        if (!token) return;
        setSaving(true);
        setMessage(null);
        setTestResult(null);
        try {
            await persistCurrentForm(token);
            setMessage({ kind: "success", text: "配置已加密保存到数据库，API Key 不会回显" });
        } catch (error) {
            setMessage({ kind: "error", text: integrationErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    }

    async function testConnection() {
        const token = validateCurrentForm();
        if (!token) return;
        setTesting(true);
        setMessage(null);
        setTestResult(null);
        try {
            await persistCurrentForm(token);
            const result = await testOpenAICompatibleConfig(token);
            setTestResult(result);
        } catch (error) {
            setMessage({ kind: "error", text: integrationErrorMessage(error) });
        } finally {
            setTesting(false);
        }
    }

    function validateCurrentForm(): string | null {
        if (!csrfToken) {
            setMessage({ kind: "error", text: "当前会话缺少写入令牌，请重新登录" });
            return null;
        }
        if (agentName.trim().length < 2 || !baseUrl.trim() || !modelId.trim()) {
            setMessage({ kind: "error", text: "请先填写 Agent 名称、Base URL 和模型 ID" });
            return null;
        }
        if (!config?.api_key_configured && apiKey.length < 8) {
            setMessage({ kind: "error", text: "首次配置请输入至少 8 位 API Key" });
            return null;
        }
        return csrfToken;
    }

    async function persistCurrentForm(token: string): Promise<void> {
        const saved = await saveOpenAICompatibleConfig(token, {
            agent_name: agentName.trim(),
            base_url: baseUrl.trim(),
            model_id: modelId.trim(),
            ...(apiKey ? { api_key: apiKey } : {}),
        });
        setConfig(saved);
        setAgentName(saved.agent_name ?? "");
        setBaseUrl(saved.base_url ?? "");
        setModelId(saved.model_id ?? "");
        setApiKey("");
        await onAgentSaved?.();
    }

    return (
        <section className="workspace-view integrations-view" aria-labelledby="integrations-title">
            <div className="view-heading">
                <div>
                    <p className="section-kicker">PLATFORM CONFIGURATION</p>
                    <h1 id="integrations-title">集成与模型</h1>
                    <p>配置 OpenAI 兼容网关。长期密钥不会进入浏览器存储，也不会通过查询接口返回。</p>
                </div>
                <span className={`integration-status ${config?.configured ? "configured" : "empty"}`}>
                    {config?.configured ? "● 已配置" : "○ 未配置"}
                </span>
            </div>

            <div className="integration-layout">
                <form className="card integration-form" onSubmit={save}>
                    <div className="card-head">
                        <div>
                            <p>OPENAI COMPATIBLE</p>
                            <h2>模型网关配置</h2>
                        </div>
                    </div>

                    {loading ? (
                        <div className="integration-loading" role="status">
                            正在读取配置…
                        </div>
                    ) : (
                        <fieldset disabled={saving || testing}>
                            <label>
                                <span>Agent 名称</span>
                                <input
                                    maxLength={80}
                                    minLength={2}
                                    onChange={(event) => setAgentName(event.target.value)}
                                    placeholder="例如：需求分析 Agent"
                                    required
                                    value={agentName}
                                />
                                <small>保存后会作为模型 Agent 加入 Agent 资源池。</small>
                            </label>
                            <label>
                                <span>第三方 Base URL</span>
                                <input
                                    inputMode="url"
                                    maxLength={500}
                                    onChange={(event) => setBaseUrl(event.target.value)}
                                    placeholder="https://router.vinabot.ai/v1"
                                    required
                                    type="url"
                                    value={baseUrl}
                                />
                                <small>必须使用 HTTPS；保存时拒绝本机和私网地址。</small>
                            </label>
                            <label>
                                <span>模型 ID</span>
                                <input
                                    maxLength={200}
                                    onChange={(event) => setModelId(event.target.value)}
                                    placeholder="gpt-5.6-sol"
                                    required
                                    value={modelId}
                                />
                            </label>
                            <label>
                                <span>API Key</span>
                                <input
                                    autoComplete="off"
                                    maxLength={4096}
                                    minLength={8}
                                    onChange={(event) => setApiKey(event.target.value)}
                                    placeholder={config?.api_key_configured ? "留空则继续使用现有 Key" : "sk-xxxxxxxx"}
                                    type="password"
                                    value={apiKey}
                                />
                                <small>
                                    {config?.api_key_configured
                                        ? `当前密钥：${config.api_key_masked}`
                                        : "Key 仅提交后端；首次保存必填，页面不会回填。"}
                                </small>
                            </label>
                            <div className="integration-actions">
                                <button className="button primary" disabled={saving} type="submit">
                                    {saving ? "保存中…" : "保存配置"}
                                </button>
                                <button
                                    className="button secondary"
                                    disabled={saving || testing}
                                    onClick={() => void testConnection()}
                                    type="button"
                                >
                                    {testing ? "保存并测试中…" : "测试连接"}
                                </button>
                            </div>
                            <small className="integration-action-hint">测试连接会先自动保存当前表单。</small>
                        </fieldset>
                    )}

                    {message && (
                        <div className={`form-message ${message.kind}`} role={message.kind === "error" ? "alert" : "status"}>
                            {message.text}
                        </div>
                    )}
                </form>

                <aside className="card integration-summary" aria-label="配置安全边界">
                    <p className="section-kicker">SECURITY BOUNDARY</p>
                    <h2>本地隔离环境</h2>
                    <dl>
                        <div>
                            <dt>存储方式</dt>
                            <dd>数据库加密存储</dd>
                        </div>
                        <div>
                            <dt>API Key</dt>
                            <dd>{config?.api_key_masked ?? "未配置"}</dd>
                        </div>
                        <div>
                            <dt>连接测试</dt>
                            <dd>GET /models，不产生推理费用</dd>
                        </div>
                    </dl>
                    <div className="integration-boundary-note">
                        配置会在后端重启后保留；API Key 使用独立主密钥加密。生产环境启用前仍需接入专用密钥管理和出站白名单。
                    </div>
                    {testResult && (
                        <div className={`integration-test-result ${testResult.status.toLowerCase()}`} role="status">
                            <strong>{testResult.status === "SUCCESS" ? "连接验证通过" : "连接验证失败"}</strong>
                            <span>{testResult.message}</span>
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
}

function integrationErrorMessage(error: unknown): string {
    if (!(error instanceof ApiRequestError)) return "操作失败，请稍后重试";
    if (error.errorCode === "PERMISSION_DENIED") return "当前账号无权管理集成配置";
    if (error.errorCode === "INTEGRATION_TARGET_BLOCKED") return "Base URL 不允许指向本机或私网地址";
    if (error.errorCode === "INTEGRATION_HOST_UNRESOLVED") return "Base URL 的主机无法解析";
    if (error.errorCode === "API_KEY_REQUIRED") return "首次配置必须填写 API Key";
    if (error.errorCode === "CSRF_INVALID") return "会话写入令牌已失效，请重新登录";
    return error.message;
}
